import axios, {AxiosResponse} from "axios";
import {SagaIterator} from "redux-saga";
import {all, AllEffect, ForkEffect} from "@redux-saga/core/effects";
import {
    call,
    CallEffect,
    put,
    PutEffect,
    putResolve,
    select,
    SelectEffect,
    TakeEffect,
    takeEvery,
    takeLatest,
} from "redux-saga/effects";
import {
    AnyElementType,
    CompositeNavigation,
    DisplayType,
    EditorType,
    ElementsValues,
    ElementsValuesInfo,
    ElementsValuesType,
    fetchAndParseSchema,
    getToken,
    GetTokenGeneratorType,
    history,
    HttpMethod,
    isAbsoluteUrl,
    isResourceAvailable,
    isSignedResourceAvailable,
    IsSignedResourceAvailableType,
    S3FieldItemResponse,
    S3MethodItem,
    S3MultipartCommand,
    safeSaga,
    selectSignedResourceUrl,
    SET_AXIOS_ERROR,
    setAxiosError,
    SetAxiosErrorAction,
    setSignedResource,
    SetSignedResourceAction,
    SignS3ResourceResponse,
    updateJobElementsValues,
    UploadedChunk,
} from "@data-force/app";
import {SET_AXIOS_READY} from "@data-force/app/store/actions/axios";
import {FetchAndParseSchemaAction} from "@data-force/app/interfaces/store/actions/schema";
import {UploadFile} from "antd/es/upload/interface";
import Nullable from "@data-force/app/interfaces/nullable";
import {message} from "antd";
import {ContentHeadResponse} from "@data-force/app/interfaces/store/sagas/utils";
import StoreState from "../../interfaces/store/storeState";
import {
    COMPLETE_INTAKE,
    GET_ENABLE_BULK_COMPLETE,
    GET_JOB_DETAILS,
    GET_JOB_PHASE,
    GET_PROJECT_DETAILS,
    GET_SCHEMA,
    GET_WORKFLOW_DEFINITIONS,
    INIT_MANIFOLD,
    PROCESS_PUBLIC_URL,
    PROCESS_RESOURCE_URL,
    RENEW_SIGNED_MEDIA_URL,
    SIGN_RESOURCE,
} from "../actions/availableActions";
import {CompleteIntakeAction, GetTaskListAction} from "../../interfaces/store/actions/task";
import {
    ChunkData,
    ChunkUploadInfo,
    ContinueMultipartUploadData,
    MultipartUploadData,
    FileUploadData,
    FileUploadInfo,
    SignS3Resource,
    UploadedFile,
    InitializeMultipartUploadData,
} from "../../interfaces/store/sagas/main";
import {getSchema} from "../actions/schema";
import {GetSchemaAction} from "../../interfaces/store/actions/schema";
import HistoryParams from "../../interfaces/historyParams";
import {
    getHistoryParams,
    getMediaUrls,
    getNavigationElements,
    mapTasksToBucketKeys,
    mergeUploadBuckets,
} from "../../utils/common";
import {
    GetProjectDetailsAction,
    GetWorkflowDefinitionsAction,
    SetEnableBulkCompletePhaseAction,
    SetProjectDetailsAction,
    SetWorkflowDefinitionsAction,
} from "../../interfaces/store/actions/project";
import {PhaseConfig, ProjectDetails} from "../../interfaces/store/actions/payload/project";
import {
    getProjectDetails,
    getWorkflowDefinitions,
    setEnableBulkCompletePhaseAction,
    setProjectDetails,
    setWorkflowDefinitions,
} from "../actions/project";
import {
    addFailedFileUploadPath,
    addUploadedChunk,
    createUploadedChunksHolder,
    getJobDetails,
    getJobPhase,
    incrementUploadedFiles,
    initManifold,
    legacySignResource,
    processPublicUrl,
    removeFailedFileUploadPath,
    setJobDetails,
    setJobMaskData,
    setJobPhase,
    setPhaseDetails,
    signResource,
    updateFailedUploadDialog,
} from "../actions/job";
import {
    LegacySignResourceAction,
    ProcessPublicUrlAction,
    ProcessResourceUrlAction,
    SetJobDetailsAction,
    SetJobPhaseAction,
    SetPhaseDetailsAction,
    SignResourceAction,
} from "../../interfaces/store/actions/job";
import {
    GetPhaseAccessStatusResponse,
    GetPhaseDetailsResponse,
    JobDetails,
    JobPhase,
    MultipartUploads,
    SignResourcePayload,
    UploadedChunksHolder,
} from "../../interfaces/store/actions/payload/job";
import {
    CompleteIntakeData,
    CompleteIntakePayload,
    CompleteType,
    GetTaskResponse,
    Task, TaskData,
    TaskEntity,
} from "../../interfaces/store/actions/payload/task";
import {WorkflowDefinition} from "../../interfaces/store/actions/payload/workflowDefinitions";
import {FILE_CHUNK_SIZE, PUBLIC_S3_BUCKET, REJECT_EVENT} from "../../utils/constants";
import {
    getAnnotationUrl,
    getFluxUrl,
    getJobMetadata,
    handleValidationError,
    metadataHasErrors,
    saveAndReturnActiveTask,
    saveBatchFieldsData,
    signFileResourceHandler,
    SignTaskFileGeneratorType,
    signTaskFileHandler,
} from "./common";
import {clearAlerts, setNavigationElements, setSignedResourcesMap} from "../actions/layout";
import {
    RenewSignedMediaUrlAction,
    SetNavigationElementsAction,
    SetSignedResourcesMapAction,
} from "../../interfaces/store/actions/layout";
import {SignedResourcesMap} from "../../interfaces/store/reducers/layoutState";
import {completeIntake, getTaskList} from "../actions/task";

import {SelectedFiles, UploadsBucket} from "../../interfaces/store/reducers/uploadFilesState";

import {fetchAllTasks, FetchAllTasksGenerator} from "./task";
import {TaskIdBucketKey} from "../../interfaces/store/actions/payload/fileValidation";
import {removeSelectedFiles, signFileResource, signTaskFile} from "../actions/uploadFiles";
import {createSignedResourceKey} from "../../components/Markdown/utils";
import {ContentGetResponse} from "../../interfaces/store/sagas/task";

const axiosUpload = axios.create();

type SetAxiosReadyGenerator = Generator<SelectEffect | PutEffect,
void,
Nullable<Task>>;

function* setAxiosReadyHandler(): SetAxiosReadyGenerator {
    const activeTask: Nullable<Task> = (
        yield select((state: StoreState): Nullable<Task> => state.job.activeTask)
    ) as Nullable<Task>;
    if (!activeTask) {
        yield put(initManifold());
    }
}

function* initManifoldHandler(): SagaIterator {
    const historyParams: Nullable<HistoryParams> = getHistoryParams();
    if (historyParams) {
        const {projectId}: HistoryParams = historyParams;
        yield all([
            put(getProjectDetails(projectId)),
            put(getJobPhase()),
            put(getJobDetails()),
        ]);
        yield put(setJobMaskData({visible: false}));
    }
}

type GetProjectDetailsGenerator = Generator<SelectEffect |
Promise<AxiosResponse<ProjectDetails>> |
AllEffect<PutEffect<GetSchemaAction | SetProjectDetailsAction | GetWorkflowDefinitionsAction> | TakeEffect>,
void, string | AxiosResponse<ProjectDetails>>;

function* getProjectDetailsHandler({payload}: GetProjectDetailsAction): GetProjectDetailsGenerator {
    const fluxUrl: string = (yield getFluxUrl()) as string;
    const {data}: AxiosResponse<ProjectDetails> = (
        yield axios.get(`${fluxUrl}/project/${payload}`)
    ) as AxiosResponse<ProjectDetails>;

    yield all([
        put(getSchema(data.schemaId)),
        put(setProjectDetails(data)),
        put(getWorkflowDefinitions(data.type)),
    ]);
}

type GetSchemaGenerator = Generator<SelectEffect |
TakeEffect |
PutEffect<FetchAndParseSchemaAction>,
void, string>;

function* getSchemaHandler({payload}: GetSchemaAction): GetSchemaGenerator {
    const annotationUrl: string = (yield getAnnotationUrl()) as string;
    const {phase}: HistoryParams = getHistoryParams();

    yield put(fetchAndParseSchema({
        schemaId: payload,
        url: annotationUrl,
        reference: phase,
    }));
}

type GetJobPhaseStatsGenerator = Generator<SelectEffect
| Promise<AxiosResponse<JobPhase>>
| PutEffect<SetJobPhaseAction>
| CallEffect,
void,
string | AxiosResponse<JobPhase> | TaskEntity[]>;

function* getJobPhaseStatsHandler(): GetJobPhaseStatsGenerator {
    const fluxUrl: string = (yield getFluxUrl()) as string;
    const historyParams: Nullable<HistoryParams> = getHistoryParams();
    if (historyParams) {
        const {projectId, jobId, jobPhaseId}: HistoryParams = historyParams;

        const {data}: AxiosResponse<JobPhase> = (
            yield axios.get(`${fluxUrl}/project/${projectId}/job/${jobId}/phase/${jobPhaseId}`)
        ) as AxiosResponse<JobPhase>;

        yield put(setJobPhase(data));
    }
}

type GetGlobalNavigationGenerator = Generator<SelectEffect | PutEffect<SetNavigationElementsAction>>;

function* getGlobalNavigationHandler(): GetGlobalNavigationGenerator {
    const historyParams: Nullable<HistoryParams> = getHistoryParams();
    const editor: EditorType = (yield select(
        ({schema: {parsedSchemas}, project: {projectDetails}}: StoreState): EditorType => (
            parsedSchemas[`${projectDetails?.schemaId}_${historyParams?.phase}`] as EditorType
        ),
    )) as EditorType;

    if (editor && editor.layout === DisplayType.DICTATION) {
        const {batchFieldReferences, refElements}: EditorType = editor;
        const metadataInfo: ElementsValuesInfo = (yield select(
            ({schema: {elementsValuesMap}, project: {projectDetails}}: StoreState): ElementsValuesInfo => (
                {
                    elementsValuesMap,
                    elementsValuesType: ElementsValuesType.JOB,
                    metadataId: historyParams?.jobId,
                    schemaKey: `${projectDetails?.schemaId}_${historyParams?.phase}`,
                }
            ),
        )) as ElementsValuesInfo;

        const navElements: AnyElementType[] = getNavigationElements(
            [],
            batchFieldReferences!,
            refElements!,
            CompositeNavigation.GLOBAL,
            metadataInfo,
        );

        yield put(setNavigationElements(navElements));
    }
}

type GetSignedMediaUrlsHandlerGenerator = Generator<SelectEffect
| AllEffect<IsSignedResourceAvailableType>
| PutEffect<SetSignedResourcesMapAction>
| Promise<AxiosResponse<SignS3Resource>>>;

function* getSignedMediaUrlsHandler(): GetSignedMediaUrlsHandlerGenerator {
    const historyParams: Nullable<HistoryParams> = getHistoryParams();
    const editor: EditorType = (yield select(
        ({schema: {parsedSchemas}, project: {projectDetails}}: StoreState): EditorType => (
            parsedSchemas[`${projectDetails?.schemaId}_${historyParams?.phase}`] as EditorType
        ),
    )) as EditorType;

    if (editor && editor.layout === DisplayType.DICTATION) {
        const signedResourcesMap: SignedResourcesMap = {};
        const {batchFieldReferences, refElements}: EditorType = editor;
        const mediaUrls: string[] = getMediaUrls(
            [],
            batchFieldReferences!,
            refElements!,
        );
        const baseUrl: string = (yield getAnnotationUrl()) as string;

        if (baseUrl && mediaUrls.length) {
            const signedUrls: boolean[] = (yield all(
                mediaUrls.map(
                    (mediaUrl: string): IsSignedResourceAvailableType => isSignedResourceAvailable({
                        baseUrl,
                        fileReference: mediaUrl,
                    }),
                ),
            )) as boolean[];

            let mediaUrl: string = "";
            for (let index = 0; index < mediaUrls.length; index += 1) {
                mediaUrl = mediaUrls[index];
                if (signedUrls[index]) {
                    const urlAxiosResponse: AxiosResponse<SignS3Resource> = (yield axios.get(
                        `${baseUrl}/sign`, {
                            params: {
                                key: mediaUrl,
                                method: "GET",
                            },
                        },
                    )) as AxiosResponse<SignS3Resource>;

                    if (urlAxiosResponse) {
                        const {data: {contentUrl}}: AxiosResponse<SignS3Resource> = urlAxiosResponse;
                        signedResourcesMap[mediaUrl] = contentUrl;
                    }
                } else {
                    signedResourcesMap[mediaUrl] = mediaUrl;
                }
            }
        }

        yield put(setSignedResourcesMap(signedResourcesMap));
    }
}

// eslint-disable-next-line max-len
type GetInitialNavigationElementsGenerator = Generator<AllEffect<CallEffect<GetGlobalNavigationGenerator | GetSignedMediaUrlsHandlerGenerator>>>;

function* getInitialNavigationElements(): GetInitialNavigationElementsGenerator {
    yield all([
        call(getGlobalNavigationHandler),
        call(getSignedMediaUrlsHandler),
    ]);
}

type GetBatchFieldsDataGenerator = Generator<SelectEffect
| PutEffect
| CallEffect
| CallEffect<ElementsValues>
| Promise<AxiosResponse>
| GetTokenGeneratorType,
void,
string | ElementsValues>;

export function* getBatchFieldsData(): GetBatchFieldsDataGenerator {
    const jobMetadata: ElementsValues = (yield call(getJobMetadata)) as ElementsValues;
    const {jobId, phase}: HistoryParams = getHistoryParams();

    if (jobMetadata) {
        const schemaKey: string = (
            yield select(({project: {projectDetails}}: StoreState): string => (
                `${projectDetails?.schemaId}_${phase}`
            ))
        ) as string;

        yield putResolve(updateJobElementsValues({
            schemaKey,
            metadataId: jobId,
            values: jobMetadata,
        }));
        yield call(getInitialNavigationElements);
    }
}

type GetJobDetailsGenerator = Generator<SelectEffect
| Promise<AxiosResponse<JobDetails | GetPhaseAccessStatusResponse | GetPhaseDetailsResponse>>
| PutEffect<SetJobDetailsAction | GetTaskListAction | SetPhaseDetailsAction>
| CallEffect<GetBatchFieldsDataGenerator>,
void, string | AxiosResponse<JobDetails | GetPhaseDetailsResponse | GetPhaseAccessStatusResponse>>;

function* getJobDetailsHandler(): GetJobDetailsGenerator {
    const {
        projectId, jobId, phase, jobPhaseId,
    }: HistoryParams = getHistoryParams();
    const fluxUrl: string = (yield getFluxUrl()) as string;
    const axiosResponseSign: AxiosResponse<GetPhaseAccessStatusResponse> = (yield axios.get(
        `${fluxUrl}/project/${projectId}/job/${jobId}/phase/${phase}/sign`,
    )) as AxiosResponse<GetPhaseAccessStatusResponse>;

    const axiosResponsePhaseDetails: AxiosResponse<GetPhaseDetailsResponse> = (yield axios.get(
        `${fluxUrl}/project/${projectId}/job/${jobId}/phase/${jobPhaseId}`,
    )) as AxiosResponse<GetPhaseDetailsResponse>;

    if (axiosResponsePhaseDetails && axiosResponseSign) {
        const {
            data: {
                expireTimestamp,
                timeLimit,
                allowedSubmission,
                status,
            },
        }: AxiosResponse<GetPhaseDetailsResponse> = axiosResponsePhaseDetails;

        const {data: {writeAccess}}: AxiosResponse<GetPhaseAccessStatusResponse> = axiosResponseSign;

        yield put(setPhaseDetails({
            expireTimestamp, status, allowedSubmission, timeLimit, accessStatus: writeAccess,
        }));

        const {data}: AxiosResponse<JobDetails> = (
            yield axios.get(`${fluxUrl}/project/${projectId}/job/${jobId}`)
        ) as AxiosResponse<JobDetails>;
        yield put(setJobDetails({
            ...data,
        }));
        yield call(getBatchFieldsData);
        yield put(getTaskList({}));
    }
}

type SimpleUploadToS3Generator = Generator<CallEffect
| PutEffect
| Promise<AxiosResponse<SignS3Resource>>
| SignTaskFileGeneratorType,
void,
SignS3ResourceResponse>;

function* simpleUploadToS3(fileUploadData: FileUploadData): SimpleUploadToS3Generator {
    const {
        uploadFile, historyParams, taskEntity, fileUrlReference,
    }: FileUploadData = fileUploadData;
    const {originFileObj, type}: UploadFile = uploadFile;

    const signResponse: SignS3ResourceResponse = (yield call(signTaskFileHandler, signTaskFile({
        historyParams,
        taskEntity,
        fieldItems: [{
            fileField: fileUrlReference,
            methods: [HttpMethod.PUT],
        }],
    }))) as SignS3ResourceResponse;

    const fileUploadUrl: Nullable<string> = signResponse.entityItems[0]?.fieldItems[0]?.methodItems.find(
        (methodItem: S3MethodItem): boolean => methodItem.method === HttpMethod.PUT
    )?.token;
    if (!fileUploadUrl) {
        throw Error("Invalid file sign!");
    }

    yield axiosUpload.put(fileUploadUrl, originFileObj!, {
        headers: {
            "Content-Type": type,
        },
    });
}

type ChunkUploadGenerator = Generator<Promise<AxiosResponse> | PutEffect, UploadedChunk, AxiosResponse>;

function* uploadChunk(chunkUploadInfo: ChunkUploadInfo): ChunkUploadGenerator {
    const {
        chunkData: {
            chunk, uploadUrl, partNumber,
        },
        type,
        fileValue,
        isRetry,
        uploadedChunks,
    }: ChunkUploadInfo = chunkUploadInfo;

    let uploadedChunk: Nullable<UploadedChunk> = isRetry && uploadedChunks.length
        ? uploadedChunks.find((uploaded: UploadedChunk) => uploaded.partNumber === partNumber)
        : null;

    if (!uploadedChunk) {
        const {headers: {etag}}: AxiosResponse = (yield axiosUpload.put(uploadUrl, chunk, {
            headers: {
                "Content-Type": type,
            },
        })) as AxiosResponse;

        uploadedChunk = {eTag: etag, partNumber};

        yield put(addUploadedChunk({
            fileValue,
            chunk: uploadedChunk,
        }));
    }

    return uploadedChunk;
}

type InitializeMultipartUploadGenerator = Generator<SelectEffect
| CallEffect
| PutEffect
| Promise<SignTaskFileGeneratorType>,
MultipartUploadData,
MultipartUploads
| SignS3ResourceResponse>;

function* initializeMultipartUpload({
    numberOfChunks,
    fileUrlReference,
    historyParams,
    taskEntity,
    alreadyUploaded,
}: InitializeMultipartUploadData): InitializeMultipartUploadGenerator {
    const chunkIds: number[] = Array.from(Array(numberOfChunks).keys())
        .map((x: number): number => x + 1);

    // start multipart upload
    const initialize: SignS3ResourceResponse = (yield call(signTaskFileHandler, signTaskFile({
        historyParams,
        taskEntity,
        fieldItems: [{
            fileField: fileUrlReference,
            multipartCommand: S3MultipartCommand.INITIATE,
            parts: chunkIds,
        }],
    }))) as SignS3ResourceResponse;

    const fieldItem: Nullable<S3FieldItemResponse> = initialize.entityItems[0]?.fieldItems[0];
    if (!fieldItem) {
        throw Error("Invalid file sign!");
    }
    const {
        fileValue: uploadFileValue,
        multipartId: uploadMultipartId,
        methodItems,
    }: S3FieldItemResponse = fieldItem;
    if (!uploadFileValue || !uploadMultipartId) {
        throw Error("Invalid file sign!");
    }

    yield putResolve(createUploadedChunksHolder({
        fileValue: uploadFileValue.split("/").pop() as string,
        multipartId: uploadMultipartId,
    }));

    return {
        multipartId: uploadMultipartId,
        alreadyUploaded,
        chunkMethodItems: methodItems,
    };
}

type ContinueMultipartUploadGenerator = Generator<SelectEffect
| CallEffect
| Promise<SignTaskFileGeneratorType>,
MultipartUploadData,
| UploadedChunk[]
| MultipartUploads
| SignS3ResourceResponse>;

function* continueMultipartUpload({
    fileValue,
    numberOfChunks,
    fileUrlReference,
    historyParams,
    taskEntity,
}: ContinueMultipartUploadData): ContinueMultipartUploadGenerator {
    let multipartId: string = "";
    let alreadyUploaded: UploadedChunk[] = [];
    let uploadedChunk: Nullable<UploadedChunk>;
    let chunkMethodItems: S3MethodItem[] = [];
    const chunkIds: number[] = [];

    const multipartUploads: MultipartUploads = (
        yield select((state: StoreState): MultipartUploads => state.job.multipartUploads)
    ) as MultipartUploads;
    const uploadedChunksHolder: UploadedChunksHolder = multipartUploads[fileValue];
    if (uploadedChunksHolder) {
        multipartId = uploadedChunksHolder.multipartId || "";
        alreadyUploaded = uploadedChunksHolder.uploadedChunks || [];
    }

    if (multipartId) {
        for (let index = 1; index <= numberOfChunks; index += 1) {
            uploadedChunk = alreadyUploaded.find(
                (uploaded: UploadedChunk): boolean => uploaded.partNumber === index,
            );
            if (!uploadedChunk) {
                chunkIds.push(index);
            }
        }

        // continue multipart upload
        const resume: SignS3ResourceResponse = (yield call(signTaskFileHandler, signTaskFile({
            historyParams,
            taskEntity,
            fieldItems: [{
                fileField: fileUrlReference,
                multipartId,
                parts: chunkIds,
            }],
        }))) as SignS3ResourceResponse;

        const missingChunkItems: Nullable<S3MethodItem[]> = resume.entityItems[0]?.fieldItems[0]?.methodItems;
        if (!missingChunkItems) {
            throw Error("Invalid file sign!");
        }
        chunkMethodItems = missingChunkItems;
    }

    return {
        multipartId, alreadyUploaded, chunkMethodItems,
    };
}

type MultipartUploadToS3Generator = Generator<SelectEffect
| PutEffect
| CallEffect
| AllEffect<ChunkUploadGenerator>
| Promise<SignTaskFileGeneratorType>,
void,
MultipartUploadData
| UploadedChunk[]
| MultipartUploads
| SignS3ResourceResponse>;

function* multipartUploadToS3(fileUploadData: FileUploadData): MultipartUploadToS3Generator {
    const {
        uploadFile, isRetry, historyParams, fileUrlReference, taskEntity, taskFileValue,
    }: FileUploadData = fileUploadData;
    const {
        originFileObj, type, size,
    }: UploadFile = uploadFile;

    const numberOfChunks: number = Math.floor(size! / FILE_CHUNK_SIZE) + 1;
    const chunks: ChunkData[] = [];
    const fileValue: string = taskFileValue;
    let multipartUploadData: MultipartUploadData = {
        multipartId: "",
        alreadyUploaded: [],
        chunkMethodItems: [],
    };
    let uploadedChunk: Nullable<UploadedChunk>;

    if (isRetry) {
        // continue multipart upload
        multipartUploadData = (yield call(continueMultipartUpload, {
            fileValue,
            numberOfChunks,
            fileUrlReference,
            historyParams,
            taskEntity,
        })) as MultipartUploadData;
    }

    if (!multipartUploadData.multipartId) {
        // start multipart upload
        multipartUploadData = (yield call(initializeMultipartUpload, {
            numberOfChunks,
            fileUrlReference,
            historyParams,
            taskEntity,
            alreadyUploaded: multipartUploadData.alreadyUploaded,
        })) as MultipartUploadData;
    }

    const {multipartId, alreadyUploaded, chunkMethodItems}: MultipartUploadData = multipartUploadData;

    for (let index = 1; index <= numberOfChunks; index += 1) {
        const start: number = (index - 1) * FILE_CHUNK_SIZE;
        const end: number = (index) * FILE_CHUNK_SIZE;
        const chunk: Blob = (index < numberOfChunks) ? originFileObj!.slice(start, end) : originFileObj!.slice(start);

        uploadedChunk = isRetry && alreadyUploaded.length
            ? alreadyUploaded.find((uploaded: UploadedChunk) => uploaded.partNumber === index)
            : null;

        let uploadUrl: string = "";
        if (!uploadedChunk) {
            const chunkMethodItem: Nullable<S3MethodItem> = chunkMethodItems.length ? chunkMethodItems.shift() : null;
            if (!chunkMethodItem) {
                throw Error("Invalid file sign!");
            }
            uploadUrl = chunkMethodItem.token;
        }

        chunks.push({
            chunk, uploadUrl, partNumber: index,
        });
    }

    // upload chunks
    const uploadedChunks: UploadedChunk[] = (yield all(
        chunks.map((chunkData: ChunkData) => uploadChunk({
            chunkData,
            type: type!,
            fileValue,
            isRetry,
            uploadedChunks: alreadyUploaded,
        })),
    )) as UploadedChunk[];

    // complete multipart upload
    yield call(signTaskFileHandler, signTaskFile({
        historyParams,
        taskEntity,
        fieldItems: [{
            fileField: fileUrlReference,
            multipartCommand: S3MultipartCommand.COMPLETE,
            multipartId,
            multipartTags: uploadedChunks,
        }],
    }));
}

type UploadToS3Generator = Generator<PutEffect
| CallEffect<SimpleUploadToS3Generator | MultipartUploadToS3Generator>,
void,
string>;

function* uploadToS3(fileUploadData: FileUploadData): UploadToS3Generator {
    const {uploadFile, taskFileValue}: FileUploadData = fileUploadData;
    const {size}: UploadFile = uploadFile;

    try {
        if (size! < FILE_CHUNK_SIZE) {
            yield call(simpleUploadToS3, fileUploadData);
        } else {
            yield call(multipartUploadToS3, fileUploadData);
        }
        yield putResolve(removeFailedFileUploadPath(taskFileValue));
        yield put(incrementUploadedFiles());
    } catch (error) {
        yield putResolve(addFailedFileUploadPath(taskFileValue));
    }
}

type FileUploadGenerator = Generator<CallEffect
| SelectEffect
| Promise<AxiosResponse<GetTaskResponse>>
| GetTokenGeneratorType,
UploadedFile,
string | AxiosResponse<GetTaskResponse>>;

function* fileUploadHandler(fileUploadInfo: FileUploadInfo): FileUploadGenerator {
    const {
        failedFileUploadPaths,
        isRetry,
        uploadFile,
        historyParams,
        taskEntity,
        fileUrlReference,
        taskFileValue,
    }: FileUploadInfo = fileUploadInfo;
    if (!taskFileValue) {
        throw Error("File upload not possible");
    }

    if (isRetry) {
        const failedUploadIndex: number = failedFileUploadPaths.findIndex(
            (uploadPath: string) => uploadPath === taskFileValue,
        );
        if (failedUploadIndex >= 0) {
            yield call(uploadToS3, {
                uploadFile, isRetry, historyParams, fileUrlReference, taskEntity, taskFileValue,
            });
        }
    } else {
        yield call(uploadToS3, {
            uploadFile, isRetry, historyParams, fileUrlReference, taskEntity, taskFileValue,
        });
    }
    return {fileValue: taskFileValue, uploadFile};
}

type CompleteIntakeHandler = Generator<FetchAllTasksGenerator
| SelectEffect
| PutEffect
| CallEffect
| AllEffect<FileUploadGenerator | GetTokenGeneratorType | string | Promise<Nullable<AxiosResponse<Task>>>>
| Promise<AxiosResponse>
| GetTokenGeneratorType
| SetJobDetailsAction
| TakeEffect,
void,
Task[]
| UploadFile[]
| string[]
| string
| ElementsValues
| EditorType
| Task
| SelectedFiles
| UploadedFile[]
| Record<string, Nullable<AxiosResponse<Task>>>
| AxiosResponse>;

function* completeIntakeHandler(payload: CompleteIntakePayload): CompleteIntakeHandler {
    const {
        event, isRetry, completeType, taskList,
    }: CompleteIntakePayload = payload;
    yield put(updateFailedUploadDialog({visible: false}));
    yield put(setJobMaskData({
        visible: true,
        messageId: "fileUploadInProgress",
        displaySpinner: true,
        completeType,
    }));

    const selectedFiles: SelectedFiles = (
        yield select((state: StoreState): SelectedFiles => state.uploadFiles.selectedFiles)
    ) as SelectedFiles;
    const uploadFiles: UploadsBucket = mergeUploadBuckets(selectedFiles);
    const taskIdBucketKey: TaskIdBucketKey = mapTasksToBucketKeys(selectedFiles);
    const historyParams: HistoryParams = getHistoryParams();
    const {projectId, jobId, phase}: HistoryParams = historyParams;

    const annotationUrl: string = (yield getAnnotationUrl()) as string;
    const {fileUrlReference, fileNameReference, uploadReference}: EditorType = (
        yield select(({schema: {parsedSchemas}, project: {projectDetails}}: StoreState): EditorType => (
            parsedSchemas[`${projectDetails!.schemaId}_${phase}`] as EditorType)!)
    ) as EditorType;
    if (!fileUrlReference || !fileNameReference) {
        throw Error("Invalid schema config!");
    }

    let tasks: TaskEntity[] = [];
    let isNetworkError: boolean = false;
    let saveTasksResponse: Nullable<AxiosResponse>;
    try {
        tasks = completeType === CompleteType.BULK
            ? (yield fetchAllTasks()) as TaskEntity[]
            : taskList;
        // Task can be uploaded only if corresponding file is selected
        const uploadTaskIds: string[] = Object.keys(uploadFiles);
        tasks = tasks.filter((task: TaskEntity): boolean => uploadTaskIds.includes(task.payload));

        const tokens: string[] = (yield all(
            tasks.map(
                ({id}: TaskEntity): GetTokenGeneratorType => getToken({
                    projectId, jobId, taskId: id, phase, forceTokenRefresh: true,
                }),
            ),
        )) as string[];

        saveTasksResponse = (
            yield axios.put(
                `${annotationUrl}/task/update`, {
                    tasks: tokens.map((token: string, index: number) => {
                        const updateTaskFields: string [] = [fileUrlReference, fileNameReference];
                        const taskId: string = tasks[index].payload;
                        const data: TaskData = {};
                        data[fileUrlReference] = uploadFiles[taskId].name;
                        data[fileNameReference] = uploadFiles[taskId].name;
                        if (uploadReference) {
                            data[uploadReference] = taskIdBucketKey[taskId];
                            updateTaskFields.push(uploadReference);
                        }

                        return {token, data, fields: [...updateTaskFields]};
                    }),
                },
            )
        ) as Nullable<AxiosResponse>;
    } catch (error) {
        isNetworkError = true;
    }

    if (saveTasksResponse) {
        if (saveTasksResponse.status !== 200) {
            throw Error("Error while saving tasks");
        }

        let failedFileUploadPaths: string[] = [];
        if (isRetry) {
            failedFileUploadPaths = (
                yield select((state: StoreState): string[] => state.job.failedFileUploadPaths)
            ) as string[];
        }

        const uploadedFiles: UploadedFile[] = (yield all(
            tasks.map((taskEntity: TaskEntity): FileUploadGenerator => fileUploadHandler({
                uploadFile: uploadFiles[taskEntity.payload],
                isRetry,
                failedFileUploadPaths,
                historyParams,
                taskEntity,
                fileUrlReference,
                taskFileValue: uploadFiles[taskEntity.payload]?.name,
            })),
        )) as UploadedFile[];

        failedFileUploadPaths = (
            yield select((state: StoreState): string[] => state.job.failedFileUploadPaths)
        ) as string[];

        if (failedFileUploadPaths.length === 0 && !isNetworkError && navigator.onLine) {
            yield put(setJobMaskData({messageId: "updatingTasks"}));

            try {
                yield put(setJobMaskData({messageId: "completingIntake"}));

                const fluxUrl: string = (yield getFluxUrl()) as string;

                const completeData: CompleteIntakeData = {
                    event,
                    phase,
                };
                if (completeType === CompleteType.SINGLE) {
                    completeData.taskIds = [taskList[0].id];
                }

                yield axios.post(
                    `${fluxUrl}/project/${projectId}/job/${jobId}/complete`, completeData,
                );

                const files: UploadFile[] = completeType === CompleteType.SINGLE
                    ? uploadedFiles.map((uploadedFile: UploadedFile): UploadFile => uploadedFile.uploadFile)
                    : Object.values(uploadFiles);
                yield put(removeSelectedFiles(files));
            } catch (e) {
                isNetworkError = true;
            }
            if (completeType === CompleteType.SINGLE) {
                yield put(getTaskList({}));
            }
            yield put(getJobPhase());
        }

        if (failedFileUploadPaths.length > 0 || isNetworkError || !navigator.onLine) {
            yield put(setJobMaskData({messageId: "", displaySpinner: false}));
            if (isNetworkError || !navigator.onLine) {
                yield put(updateFailedUploadDialog({
                    visible: true,
                    event,
                    completeType,
                }));
            } else {
                yield put(completeIntake({
                    event,
                    isRetry: true,
                    completeType,
                    taskList,
                }));
            }
        }
    }

    yield put(setJobMaskData({visible: false, messageId: "", displaySpinner: false}));
}

type SaveTaskAndCompleteIntakeGenerator = Generator<SelectEffect
| CallEffect
| PutEffect
| TakeEffect,
void,
Task | boolean | TaskEntity[]>;

function* saveAllAndCompleteIntake({payload}: CompleteIntakeAction): SaveTaskAndCompleteIntakeGenerator {
    // save active task
    yield put(setJobMaskData({visible: true, messageId: "processingFormData", displaySpinner: true}));
    message.destroy();
    yield putResolve(clearAlerts());

    const {isSubmitAfterExpiry}: CompleteIntakePayload = payload;
    const activeTask: Task = (yield call(saveAndReturnActiveTask, !!isSubmitAfterExpiry)) as Task;
    const {id, valid: validTask}: Task = activeTask;

    const {jobId}: HistoryParams = getHistoryParams();
    const validJob: boolean = (
        yield call(metadataHasErrors, {
            metadataId: jobId,
            elementsValuesType: ElementsValuesType.JOB,
        })
    ) as boolean;

    const taskList: TaskEntity[] = (
        yield select((state: StoreState): TaskEntity[] => state.job.tasks)
    ) as TaskEntity[];
    const activeTaskEntity: Nullable<TaskEntity> = taskList.find(
        (task: TaskEntity): boolean => task.payload === id,
    );

    if (payload.event === REJECT_EVENT && validJob && activeTaskEntity) {
        let completePayload: CompleteIntakePayload = {...payload};
        const {completeType}: CompleteIntakePayload = payload;
        if (completeType === CompleteType.SINGLE) {
            completePayload = {
                ...completePayload,
                taskList: [activeTaskEntity],
            };
        }
        yield call(completeIntakeHandler, completePayload);
        if (completeType === CompleteType.BULK) {
            yield put(getTaskList({}));
        }
    } else if (validTask && validJob && activeTaskEntity) {
        // save job metadata
        yield call(saveBatchFieldsData, !!isSubmitAfterExpiry);

        let completePayload: CompleteIntakePayload = {...payload};
        const {completeType}: CompleteIntakePayload = payload;
        if (completeType === CompleteType.SINGLE) {
            completePayload = {
                ...completePayload,
                taskList: [activeTaskEntity],
            };
        }

        yield call(completeIntakeHandler, completePayload);
        if (completeType === CompleteType.BULK) {
            yield put(getTaskList({}));
        }
    } else {
        yield call(handleValidationError, id);
        yield put(setJobMaskData({visible: false}));
    }
}

function* handleAxiosErrors({payload}: SetAxiosErrorAction): IterableIterator<PutEffect<SetAxiosErrorAction>> {
    if (payload && payload.response!.status === 404) {
        const historyParams: Nullable<HistoryParams> = getHistoryParams();
        history.push(`/${historyParams?.idpId}/`);
        yield put(setAxiosError(null));
    } else if (payload && payload.response!.status === 403) {
        history.push("/unauthorized");
        yield put(setAxiosError(null));
    }
}

type GetWorkflowDefinitionsGenerator = Generator<SelectEffect
| Promise<AxiosResponse<WorkflowDefinition[]>>
| PutEffect<SetWorkflowDefinitionsAction>,
void, string | AxiosResponse<WorkflowDefinition[]>>;

function* getWorkflowDefinitionsHandler({payload}: GetWorkflowDefinitionsAction): GetWorkflowDefinitionsGenerator {
    const fluxUrl: string = (yield getFluxUrl()) as string;
    const {data}: AxiosResponse<WorkflowDefinition[]> = (
        yield axios.get(`${fluxUrl}/workflow-definition`, {
            params: {
                projectType: payload,
            },
        })) as AxiosResponse<WorkflowDefinition[]>;

    if (data) {
        yield put(setWorkflowDefinitions(data || []));
    }
}

type RenewSignedMediaUrlHandlerGenerator = Generator<SelectEffect
| CallEffect<IsSignedResourceAvailableType>
| PutEffect<SetSignedResourcesMapAction>
| Promise<AxiosResponse<SignS3Resource>>>;

function* renewSignedMediaUrlHandler({payload}: RenewSignedMediaUrlAction): RenewSignedMediaUrlHandlerGenerator {
    const historyParams: Nullable<HistoryParams> = getHistoryParams();
    const editor: EditorType = (yield select(
        ({schema: {parsedSchemas}, project: {projectDetails}}: StoreState): EditorType => (
            parsedSchemas[`${projectDetails?.schemaId}_${historyParams?.phase}`] as EditorType
        ),
    )) as EditorType;

    if (editor && editor.layout === DisplayType.DICTATION) {
        const resourcesMap: SignedResourcesMap = (yield select(
            ({layout: {content: {signedResourcesMap}}}: StoreState): SignedResourcesMap => signedResourcesMap,
        )) as SignedResourcesMap;

        const baseUrl: string = (yield getAnnotationUrl()) as string;
        if (baseUrl) {
            const contentAvailable = yield call(isSignedResourceAvailable, {baseUrl, fileReference: payload});
            if (contentAvailable) {
                const urlAxiosResponse: AxiosResponse<SignS3Resource> = (yield axios.get(
                    `${baseUrl}/sign`, {
                        params: {
                            key: payload,
                            method: "GET",
                        },
                    },
                )) as AxiosResponse<SignS3Resource>;

                if (urlAxiosResponse) {
                    const {data: {contentUrl}}: AxiosResponse<SignS3Resource> = urlAxiosResponse;
                    resourcesMap[payload] = contentUrl;
                }
            } else {
                resourcesMap[payload] = payload;
            }

            yield put(setSignedResourcesMap(resourcesMap));
        }
    }
}

type SignResourcesHandlerGenerator = Generator<CallEffect
| PutEffect<SetSignedResourceAction>
| SignTaskFileGeneratorType,
void,
SignS3ResourceResponse | boolean>;

function* signResourceHandler({
    payload: {
        signType,
        fileReference,
    },
}: SignResourceAction): SignResourcesHandlerGenerator {
    const signResponse: SignS3ResourceResponse = (yield call(signFileResourceHandler, signFileResource({
        fileReference,
        signType,
    }))) as SignS3ResourceResponse;
    const contentAvailableUrl: Nullable<string> = signResponse
        .entityItems[0]?.fieldItems[0]?.methodItems.find(
        (methodItem: S3MethodItem): boolean => methodItem.method === HttpMethod.HEAD
    )?.token;
    if (!contentAvailableUrl) {
        throw Error("Invalid file sign!");
    }
    const resourceAvailable: boolean = (yield call(isResourceAvailable, contentAvailableUrl)) as boolean;

    if (resourceAvailable) {
        const contentUrl: Nullable<string> = signResponse
            .entityItems[0]?.fieldItems[0]?.methodItems.find(
            (methodItem: S3MethodItem): boolean => methodItem.method === HttpMethod.GET
        )?.token;
        if (!contentUrl) {
            throw Error("Invalid file sign!");
        }
        yield put(setSignedResource({
            resourceKey: createSignedResourceKey({fileReference, signType}),
            contentUrl,
        }));
    }
}

type ProcessPublicUrlHandlerGenerator = Generator<PutEffect<SetSignedResourceAction>,
void,
string | undefined>;

function* processPublicUrlHandler({
    payload: {
        signType,
        fileReference,
    },
}: ProcessPublicUrlAction): ProcessPublicUrlHandlerGenerator {
    const resourceKey: string = createSignedResourceKey({
        fileReference,
        signType,
    });

    if (isAbsoluteUrl(fileReference)) {
        yield put(setSignedResource({
            resourceKey,
            contentUrl: fileReference,
        }));
    } else {
        yield put(setSignedResource({
            resourceKey,
            contentUrl: `${document.location.origin}${fileReference}`,
        }));
    }
}

type LegacySignResourcesHandlerIteratorType =
SelectEffect
| PutEffect
| CallEffect<boolean | IsSignedResourceAvailableType>
| Promise<AxiosResponse<ContentHeadResponse>>;

function* legacySignResourceHandler({
    payload: {
        signType,
        fileReference: fileUrl,
    },
}: LegacySignResourceAction): IterableIterator<LegacySignResourcesHandlerIteratorType> {
    const baseUrl: Nullable<string> = yield getAnnotationUrl();
    const beginningOfUrl: string = `${document.location.origin}${document.location.pathname}`;
    const fileReference: string = fileUrl.replace(beginningOfUrl, "");

    // @ts-ignore
    const resourceAvailable: Nullable<CallEffect<boolean>> = yield call(isSignedResourceAvailable, {
        baseUrl,
        fileReference,
    });

    if (resourceAvailable) {
        const urlAxiosResponse: Nullable<AxiosResponse<ContentGetResponse>> = yield axios.get(`${baseUrl}/sign`, {
            params: {
                key: fileReference,
                method: "GET",
            },
        });

        if (urlAxiosResponse) {
            const {data: {contentUrl}}: AxiosResponse<ContentGetResponse> = urlAxiosResponse;
            if (!contentUrl) {
                throw Error("Invalid file sign!");
            }
            yield put(setSignedResource({
                resourceKey: createSignedResourceKey({
                    fileReference: fileUrl,
                    signType,
                }),
                contentUrl,
            }));
        }
    }
}

type ProcessResourceUrlHandler = Generator<SelectEffect | CallEffect, void>;

function* processResourceUrlHandler({payload}: ProcessResourceUrlAction): ProcessResourceUrlHandler {
    const {
        fileReference, isLegacySign, signType, resign,
    }: SignResourcePayload = payload;
    const resourceKey: string = createSignedResourceKey({
        fileReference,
        signType,
    });
    const processedUrl: string | undefined = (
        yield select(selectSignedResourceUrl(resourceKey))
    ) as string | undefined;

    if (!processedUrl || resign) {
        if (isLegacySign) {
            yield call(legacySignResourceHandler, legacySignResource(payload));
        } else if (isAbsoluteUrl(fileReference) && PUBLIC_S3_BUCKET.test(fileReference)) {
            yield call(processPublicUrlHandler, processPublicUrl(payload));
        } else {
            yield call(signResourceHandler, signResource(payload));
        }
    }
}

type GetEnableBulkCompletePhaseHandlerGenerator = Generator<SelectEffect
| PutEffect<SetEnableBulkCompletePhaseAction>
| Promise<Nullable<AxiosResponse<PhaseConfig[]>>>,
void, string | boolean | AxiosResponse<PhaseConfig[]> | Nullable<HistoryParams>>;

function* getEnableBulkCompletePhaseHandler(): GetEnableBulkCompletePhaseHandlerGenerator {
    const fluxUrl: string = (yield getFluxUrl()) as string;
    const axiosResponse: Nullable<AxiosResponse<PhaseConfig[]>> = (
        yield axios.get(`${fluxUrl}/phase-config`)
    ) as Nullable<AxiosResponse<PhaseConfig[]>>;
    const historyParams: Nullable<HistoryParams> = getHistoryParams();
    let enableBulkCompletePhaseAction: boolean = false;
    if (axiosResponse && historyParams) {
        const {data}: AxiosResponse<PhaseConfig[]> = axiosResponse;
        const {phase}: HistoryParams = historyParams;
        Object.values(data).forEach((item: PhaseConfig):void => {
            if (item.name === phase) {
                enableBulkCompletePhaseAction = item.enableBulkCompletePhaseAction;
            }
        });
    }
    yield put(setEnableBulkCompletePhaseAction(enableBulkCompletePhaseAction));
}

export function* actionWatcher(): IterableIterator<AllEffect<ForkEffect>> {
    yield all([
        takeLatest(INIT_MANIFOLD, safeSaga(initManifoldHandler)),
        takeLatest(SET_AXIOS_READY, safeSaga(setAxiosReadyHandler)),
        takeLatest(GET_PROJECT_DETAILS, safeSaga(getProjectDetailsHandler)),
        takeLatest(GET_SCHEMA, safeSaga(getSchemaHandler)),
        takeLatest(GET_JOB_PHASE, safeSaga(getJobPhaseStatsHandler)),
        takeLatest(GET_JOB_DETAILS, safeSaga(getJobDetailsHandler)),
        takeLatest(COMPLETE_INTAKE, safeSaga(saveAllAndCompleteIntake)),
        takeLatest(SET_AXIOS_ERROR, safeSaga(handleAxiosErrors)),
        takeLatest(GET_WORKFLOW_DEFINITIONS, safeSaga(getWorkflowDefinitionsHandler)),
        takeEvery(RENEW_SIGNED_MEDIA_URL, safeSaga(renewSignedMediaUrlHandler)),
        takeLatest(GET_ENABLE_BULK_COMPLETE, safeSaga(getEnableBulkCompletePhaseHandler)),
        takeEvery(PROCESS_RESOURCE_URL, safeSaga(processResourceUrlHandler)),
        takeEvery(PROCESS_PUBLIC_URL, safeSaga(processPublicUrlHandler)),
        takeEvery(SIGN_RESOURCE, safeSaga(signResourceHandler)),
    ]);
}
